import { useEffect, useState, type ReactNode } from "react";
import { checkAdminSession } from "../lib/admin-auth";

/**
 * Route guard for pages any signed-in user may view (read-only directory pages
 * like Chains / Activity / Changes). Allows EITHER a valid admin_session cookie
 * (via checkAdminSession) OR a brand storesync_session cookie (via
 * /api/auth/me). On miss, redirect to #/login — the brand login, not the admin
 * page, since brand users are the common case here.
 *
 * Mirrors RequireAdmin, just with the OR check and a different redirect target.
 */
export default function RequireSignedIn({ children }: { children: ReactNode }) {
  const [state, setState] = useState<"loading" | "ok" | "denied">("loading");

  useEffect(() => {
    let alive = true;
    (async () => {
      const admin = await checkAdminSession();
      if (!alive) return;
      if (admin) {
        setState("ok");
        return;
      }
      let brand = false;
      try {
        const res = await fetch("/api/auth/me", {
          credentials: "include",
          headers: { Accept: "application/json" },
        });
        brand = res.ok;
      } catch {
        brand = false;
      }
      if (!alive) return;
      if (brand) {
        setState("ok");
      } else {
        setState("denied");
        const here = window.location.hash || "#/";
        window.location.hash = `#/login?redirect=${encodeURIComponent(here)}`;
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  if (state === "ok") return <>{children}</>;
  return (
    <div className="flex items-center justify-center py-24 text-sm text-gray-500">
      <span className="inline-block h-4 w-4 mr-2 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
      {state === "loading" ? "Checking access…" : "Redirecting to sign in…"}
    </div>
  );
}
